import { useMemo, useState } from "react";
import {
  Box,
  Button,
  Divider,
  InputAdornment,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  TextField,
  Typography,
} from "@mui/material";
import Icon from "@mdi/react";
import { mdiAccountGroupOutline, mdiCheck, mdiPlus } from "@mdi/js";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

import GroupApi from "api/group";
import AppBar from "components/AppBar";
import AsyncAvatar from "components/AsyncAvatar";
import ChipList from "components/ChipList";
import FAB from "components/FAB";
import PageLayout from "components/PageLayout";
import SearchInput from "components/SearchInput";
import useNotifier from "hooks/useNotifier";
import getNameInitials from "utils/getNameInitials";
import compareString from "utils/compareString";
import useBoundStore from "store";
import { FriendData } from "api/friend";
import { StorageBuckets } from "api/storage";
import VirtualizedList from "components/VirtualizedList";
import styles from "./newGroup.module.scss";

const validationSchema = yup.object().shape({
  groupName: yup
    .string()
    .required("Group name is required")
    .min(2, "Group name can't be shorter than 2 characters")
    .max(30, "Group name can't be longer that 30 characters"),
});

export interface NewGroupProps {
  onAddFriends: () => void;
  onBack?: () => void;
  onCreateGroup: () => void;
}

interface FormData {
  groupName: string;
}

function NewGroup(props: NewGroupProps) {
  const { onAddFriends, onBack, onCreateGroup } = props;
  const [loading, setLoading] = useState<boolean>(false);
  const [searchedFriends, setSearchedFriends] = useState<FriendData[]>([]);
  const [selectedFriends, setSelectedFriends] = useState<FriendData[]>([]);
  const [searchString, setSearchString] = useState<string>("");
  const isMobileDevice = useBoundStore((state) => state.miscellaneous.isMobileDevice);
  const userAuth = useBoundStore((state) => state.user.auth);
  const friends = useBoundStore((state) =>
    state.appData.friends.sort((a, b) => compareString(a.name ?? "", b.name ?? ""))
  );
  const smallAvatars = useBoundStore((state) => state.miscellaneous.smallAvatars);
  const { showNotification } = useNotifier();
  const groupApi = useMemo(() => new GroupApi(showNotification), [showNotification]);
  const selectedFriendsIds = selectedFriends.map((friend) => friend.id);
  const remainingFriends = friends.filter((friend) => !selectedFriendsIds.includes(friend.id));
  const friendsToRender = searchString === "" ? remainingFriends : searchedFriends;
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: { groupName: "" },
    resolver: yupResolver(validationSchema),
  });

  /**
   * Handle friends search
   *
   * @param newSearchString
   * @param newSearchedFriends
   */
  function handleFriendsSearch(newSearchString: string, newSearchedFriends: FriendData[]): void {
    setSearchString(newSearchString);
    setSearchedFriends(
      newSearchedFriends.filter((searchedFriend) => {
        const isSelected = selectedFriends.findIndex((selectedFriend) => selectedFriend.id === searchedFriend.id);
        return isSelected === -1;
      })
    );
  }

  /**
   * Handle create new group
   *
   * @param values
   */
  async function onSubmit(values: FormData): Promise<void> {
    setLoading(true);

    const { error } = await groupApi.createGroup(
      values.groupName,
      userAuth!.id,
      selectedFriends.map((friend) => friend.uid)
    );

    setLoading(false);

    if (!error) {
      onCreateGroup();
    }
  }

  /**
   * Add friend to group
   *
   * @param selectedFriend
   */
  function handleSelectFriend(selectedFriend: FriendData): void {
    const clonedSelectedFriends = [...selectedFriends];
    clonedSelectedFriends.push(selectedFriend);

    setSelectedFriends(clonedSelectedFriends);
    setSearchString("");
  }

  /**
   * Remove friend from group
   *
   * @param removedSelectedFriend
   */
  function handleRemoveSelectedFriend(removedSelectedFriend: FriendData): void {
    const clonedFriends = [...friends];
    clonedFriends.push(removedSelectedFriend);

    const clonedSelectedFriends = [...selectedFriends].filter((friend) => friend.id !== removedSelectedFriend.id);

    setSelectedFriends(clonedSelectedFriends);
  }

  /**
   * Generate items which will be used to display de virtualized list
   *
   * @returns
   */
  function generateVirtualizedList(): JSX.Element[] {
    const vrList: JSX.Element[] = [];

    if (selectedFriends.length > 0) {
      vrList.push(
        <Box maxHeight='30vh' overflow='auto' px={2}>
          <ChipList chips={selectedFriends} onDelete={handleRemoveSelectedFriend} />
        </Box>
      );
    }

    if (friends.length) {
      vrList.push(
        <Box mt={2}>
          <SearchInput
            data={friends}
            onSearchedResults={handleFriendsSearch}
            inputProps={{
              "aria-label": "Search friends",
              placeholder: "Search friends",
            }}
            searchBy='name'
            searchString={searchString}
          />
          <Divider />
        </Box>
      );
    }

    friendsToRender.forEach((friend) => {
      vrList.push(
        <ListItemButton key={friend.id} onClick={() => handleSelectFriend(friend)}>
          <ListItemAvatar>
            <AsyncAvatar
              avatarFile={friend.avatar ? smallAvatars[friend.avatar] : undefined}
              avatarName={friend.avatar}
              placeholder={getNameInitials(friend.name)}
              bucket={StorageBuckets.PROFILES_AVATARS}
            />
          </ListItemAvatar>
          <ListItemText primary={friend.name} secondary={friend.tag} />
        </ListItemButton>
      );
    });

    if (friends.length === 0 && selectedFriends.length === 0 && searchString === "") {
      vrList.push(
        <Box p={2} display='flex' flexDirection='column' alignItems='center'>
          <Typography align='center' color='textSecondary'>
            It looks like your friends list is empty at the moment
          </Typography>
          <Button
            className={styles.addFriendsButton}
            onClick={onAddFriends}
            startIcon={<Icon path={mdiPlus} size={1} />}
            variant='contained'
          >
            Add friends
          </Button>
        </Box>
      );
    }

    if (friends.length > 0 && selectedFriends.length === friends.length && searchString === "") {
      vrList.push(
        <Box p={2}>
          <Typography align='center' color='textSecondary'>
            All friends were added to this group
          </Typography>
        </Box>
      );
    }

    if (searchedFriends.length === 0 && searchString !== "") {
      vrList.push(
        <Box p={2}>
          <Typography align='center' color='textSecondary'>
            No friends were found by this name
          </Typography>
        </Box>
      );
    }

    return vrList;
  }

  return (
    <PageLayout
      appBar={<AppBar title='New group' onBack={onBack} />}
      fab={
        <FAB
          aria-label='Create group'
          icon={mdiCheck}
          title='Check'
          loading={loading}
          onClick={handleSubmit(onSubmit)}
        />
      }
      paddingBottom={false}
    >
      <Box px={2} pt={3}>
        <form autoComplete='off' onSubmit={handleSubmit(onSubmit)}>
          <Controller
            control={control}
            name='groupName'
            render={({ field }) => (
              <TextField
                {...field}
                autoFocus
                error={Boolean(errors.groupName)}
                fullWidth
                helperText={errors.groupName?.message}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position='start'>
                      <Icon path={mdiAccountGroupOutline} title='Group' size={1} />
                    </InputAdornment>
                  ),
                }}
                label='Group name'
                placeholder='Any name you think of...'
              />
            )}
          />
        </form>
      </Box>

      <VirtualizedList
        estimateSize={56}
        height={`calc(100vh - ${isMobileDevice ? "136px" : "176px"}`}
        rows={generateVirtualizedList()}
      />
    </PageLayout>
  );
}

export default NewGroup;
